import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Skeleton } from 'antd';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCircleExclamation, faEarthAmericas } from '@fortawesome/free-solid-svg-icons';

import { IReservation } from '@share/common-types';
import { ReservationsAPI } from '@share/api';
import { FunctionalRootState } from '@share/utils';

import { parseApiDate } from '../../utils';
import { AccountEmptyState } from '../empty-state';
import { AccountTripList, YearKey } from './trip-list';

import styles from './styles.module.scss';

const PAGE_SIZE = 100;
const MAX_PAGES = 3;
const DEFAULT_PIN_COLOR = '#0a6ea8';
const SINGLE_PIN_ZOOM = 10;

interface ITrip {
  booking: IReservation;
  lat: number;
  lng: number;
  year: number;
}

const toTrip = (booking: IReservation): ITrip | null => {
  const lat = Number(booking.location?.latitude);
  const lng = Number(booking.location?.longitude);
  const checkIn = parseApiDate(booking.checkIn);
  // 0,0 is what a missing location usually comes back as; don't drop a pin in the ocean.
  if (!checkIn || !Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
    return null;
  }
  return { booking, lat, lng, year: checkIn.getFullYear() };
};

/** Past website bookings on a map (year tabs, clustered pins), above the member's full travel history list. */
export const AccountTravelHistory = (): React.ReactElement => {
  const intl = useIntl();
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);

  // Past site bookings; only the ones with a location become pins.
  const [bookings, setBookings] = useState<IReservation[]>([]);
  const trips = useMemo(() => bookings.map(toTrip).filter((trip): trip is ITrip => !!trip), [bookings]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [year, setYear] = useState<YearKey>('all');
  // Collapsed by default: the trip list is the main content, the map is an optional extra.
  const [expanded, setExpanded] = useState(false);

  const mapElement = useRef<HTMLDivElement | null>(null);
  const map = useRef<google.maps.Map | null>(null);
  const clusterer = useRef<MarkerClusterer | null>(null);
  const infoWindow = useRef<google.maps.InfoWindow | null>(null);

  const mapsAvailable = typeof window !== 'undefined' && !!window.google?.maps;
  const pinColor = account?.buttonColor || DEFAULT_PIN_COLOR;

  // Past bookings, a few pages deep, keeping only the ones with a real location.
  useEffect(() => {
    let isActive = true;

    const load = async () => {
      const collected: IReservation[] = [];
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { data } = await ReservationsAPI.getReservations({
          bookingId: 0,
          propertyName: '',
          leadName: '',
          bookingType: '',
          status: 'past',
          page,
          pageSize: PAGE_SIZE,
          orderBy: 'checkIn',
          sortOrder: 'desc',
        });
        if (data?.status) {
          throw new Error(`Reservations request failed with status ${data.status}`);
        }
        const items: IReservation[] = data?.items || [];
        collected.push(...items);
        if (items.length < PAGE_SIZE || collected.length >= (data?.totalResults || 0)) {
          break;
        }
      }
      return collected;
    };

    load()
      .then(items => {
        if (isActive) {
          setBookings(items);
          setLoading(false);
        }
      })
      .catch((error: unknown) => {
        console.error('Account hub: travel map request failed', error);
        if (isActive) {
          setFailed(true);
          setLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  const shown = useMemo(() => (year === 'all' ? trips : trips.filter(trip => trip.year === year)), [trips, year]);
  const destinations = useMemo(() => new Set(shown.map(trip => trip.booking.propertyCity || `${trip.lat},${trip.lng}`)).size, [shown]);

  const formatDate = (value: string | null | undefined): string => {
    const date = parseApiDate(value);
    return date ? intl.formatDate(date, { day: '2-digit', month: 'short', year: 'numeric' }) : '';
  };

  // Info window content is built from DOM nodes with textContent, so booking data can't inject HTML.
  const buildInfo = (trip: ITrip): HTMLElement => {
    const root = document.createElement('div');
    root.className = styles.info;
    const name = document.createElement('strong');
    name.textContent = trip.booking.propertyName || '';
    const place = document.createElement('div');
    place.textContent = trip.booking.propertyCity || '';
    const dates = document.createElement('div');
    dates.textContent = [formatDate(trip.booking.checkIn), formatDate(trip.booking.checkOut)].filter(Boolean).join(' – ');
    root.append(name, place, dates);
    return root;
  };

  // Create the map when its container is shown; drop it when the card collapses (the div unmounts).
  useEffect(() => {
    if (!expanded) {
      clusterer.current?.clearMarkers();
      clusterer.current = null;
      infoWindow.current = null;
      map.current = null;
      return;
    }
    if (!mapsAvailable || !mapElement.current || map.current) {
      return;
    }
    map.current = new google.maps.Map(mapElement.current, {
      center: { lat: 25, lng: -40 },
      zoom: 2,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: true,
      clickableIcons: false,
    });
    infoWindow.current = new google.maps.InfoWindow();
    clusterer.current = new MarkerClusterer({ map: map.current });
  }, [mapsAvailable, loading, expanded]);

  // Redraw pins whenever the selected year changes.
  useEffect(() => {
    if (!map.current || !clusterer.current) {
      return;
    }
    clusterer.current.clearMarkers();
    infoWindow.current?.close();

    const markers = shown.map(trip => {
      const marker = new google.maps.Marker({
        position: { lat: trip.lat, lng: trip.lng },
        title: trip.booking.propertyName,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: pinColor,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
        },
      });
      marker.addListener('click', () => {
        infoWindow.current?.setContent(buildInfo(trip));
        infoWindow.current?.open({ map: map.current as google.maps.Map, anchor: marker });
      });
      return marker;
    });
    clusterer.current.addMarkers(markers);

    if (markers.length === 1) {
      map.current.setCenter({ lat: shown[0].lat, lng: shown[0].lng });
      map.current.setZoom(SINGLE_PIN_ZOOM);
    } else if (markers.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      shown.forEach(trip => bounds.extend({ lat: trip.lat, lng: trip.lng }));
      map.current.fitBounds(bounds, 48);
    }
  }, [shown, pinColor, loading, expanded]);

  const renderMap = () => {
    if (loading) {
      return <div className={styles.mapSkeleton}><Skeleton.Image active /></div>;
    }
    if (failed) {
      return (
        <AccountEmptyState
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.history.map_error' })}
        />
      );
    }
    if (!trips.length) {
      return (
        <AccountEmptyState
          icon={faEarthAmericas}
          title={intl.formatMessage({ id: 'my.account.hub.history.map_empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.history.map_empty_body' })}
        />
      );
    }
    if (!mapsAvailable) {
      return <p className={styles.note}>{intl.formatMessage({ id: 'my.account.hub.history.map_unavailable' })}</p>;
    }
    return (
      <div
        ref={mapElement}
        className={styles.map}
        role="region"
        aria-label={intl.formatMessage({ id: 'my.account.hub.history.map_label' })}
      />
    );
  };

  return (
    <div className={styles.travelHistory}>
      <section className={styles.mapCard} aria-labelledby="account-hub-map-title">
        <div className={styles.mapHeader}>
          <div>
            <h2 id="account-hub-map-title" className={styles.mapTitle}>
              {intl.formatMessage({ id: 'my.account.hub.history.map_title' })}
            </h2>
            {!loading && !failed && trips.length > 0 ? (
              <p className={styles.summary}>
                {intl.formatMessage(
                  { id: 'my.account.hub.history.summary' },
                  { trips: shown.length, destinations },
                )}
              </p>
            ) : null}
          </div>
          {!loading && !failed && trips.length > 0 ? (
            <button
              type="button"
              className={styles.toggle}
              aria-expanded={expanded}
              aria-controls="account-hub-travel-map"
              onClick={() => setExpanded(value => !value)}
            >
              {intl.formatMessage({ id: expanded ? 'my.account.hub.history.hide_map' : 'my.account.hub.history.show_map' })}
              <FontAwesomeIcon icon={faChevronDown} className={expanded ? styles.chevronOpen : styles.chevron} aria-hidden="true" />
            </button>
          ) : null}
        </div>

        {/* Loading, error and empty states show inline; the map itself only when expanded. */}
        {!loading && (failed || trips.length === 0) ? renderMap() : null}
        {expanded && trips.length > 0 && !failed ? (
          <div id="account-hub-travel-map">
            {renderMap()}
            <p className={styles.note}>{intl.formatMessage({ id: 'my.account.hub.history.map_note' })}</p>
          </div>
        ) : null}
      </section>

      <section className={styles.listCard} aria-labelledby="account-hub-history-list-title">
        <h2 id="account-hub-history-list-title" className={styles.listTitle}>
          {intl.formatMessage({ id: 'my.account.hub.history.list_title' })}
        </h2>
        <AccountTripList year={year} onYearChange={setYear} />
      </section>
    </div>
  );
};
